import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';
import { VideoData } from '@lumen5/luminary';

import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { LintFinding } from '../videoLinter';

/** The asset types that carry content the agent supplies, and how to tell whether it did. */
const CONTENT_ASSET_TYPES = [
    VideoData.TEXT_ASSET_TYPE,
    VideoData.MEDIA_ASSET_TYPE,
    VideoData.ICON_ASSET_TYPE,
    VideoData.FANCY_CAPTION_ASSET_TYPE,
    VideoData.CHART_ASSET_TYPE,
] as const;

/**
 * A motion-graphics (codeo) scene: `scene.clear()`ed down to a single HtmlAsset that the design
 * tool authors separately. An empty `html` is its durable "awaiting a prompt" marker, not a scene
 * someone emptied, and the graphic is written by a tool this check knows nothing about — so these
 * scenes are outside what blankness means here and are skipped whatever state they are in.
 */
const isMotionGraphicsScene = (scene: Scene): boolean =>
    (scene.getAssetsByType(VideoData.HTML_ASSET_TYPE) ?? []).length > 0;

interface AssetLike {
    classType?: string;
    isEditable?: boolean;
    text?: string;
    sourceData?: { uuid?: string; url?: string };
}

/**
 * Whether an editable asset is actually showing something. Text needs words; media and icons need
 * a source to render. Any other content asset (a fancy caption, a chart) draws itself, so its
 * presence alone is content.
 */
const hasContent = (asset: AssetLike): boolean => {
    switch (asset.classType) {
        case VideoData.TEXT_ASSET_TYPE:
            return !!asset.text?.trim();
        case VideoData.MEDIA_ASSET_TYPE:
            return !!asset.sourceData?.uuid;
        case VideoData.ICON_ASSET_TYPE:
            return !!asset.sourceData?.url;
        default:
            return true;
    }
};

const editableContentAssets = (scene: Scene): AssetLike[] =>
    CONTENT_ASSET_TYPES.flatMap(
        (type) => (scene.getAssetsByType(type) ?? []) as unknown as AssetLike[],
    ).filter((asset) => asset.isEditable !== false);

/**
 * Error: a scene renders nothing but its background. Clearing text is a legitimate edit — a scene
 * design that also carries media reads fine as a visual-only beat — but on a text-only design the
 * text *is* the whole scene, so emptying it leaves a blank frame holding the video for its full
 * duration (AP-1143).
 *
 * Only editable assets are judged, which is what keeps the check honest without knowing anything
 * about particular designs. Motion-graphics (codeo) scenes are skipped outright. A scene with no
 * editable asset at all is skipped too: whatever it shows is fixed by its design, so there is
 * nothing the agent cleared and no edit that would fix it. That
 * covers the waveform base scenes — every one of them is entirely non-editable, the waveform being
 * the intended visual — with no waveform-specific rule. A design that draws the narration itself
 * carries an editable fancy-caption asset, which counts as content for the same reason.
 *
 * An error rather than a warning: unlike an unfilled placeholder, a blank frame is never what the
 * viewer should see, and the agent's own image review does not catch it.
 */
export function checkBlankScene(luminary: Luminary, _getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.scenes.forEach((scene) => {
        if (isMotionGraphicsScene(scene)) {
            return;
        }

        const editable = editableContentAssets(scene);
        if (editable.length === 0 || editable.some(hasContent)) {
            return;
        }

        findings.push({
            check: 'blank_scene',
            severity: 'error',
            sceneId: scene.id,
            message:
                'This scene renders nothing but its background — every asset you can fill is empty, ' +
                'so the video holds on a blank frame for the whole scene. Either put content back on ' +
                `it (${TOOL_NAMES.setText}, ${TOOL_NAMES.setMedia}, ${TOOL_NAMES.setIcon}), or, if this ` +
                'beat is meant to be visual-only, replace it with a design that carries media ' +
                `(${TOOL_NAMES.deleteScene}, then ${TOOL_NAMES.addVoiceoverScene}/${TOOL_NAMES.addScene} ` +
                "with a scene_design_id that includes a media asset — a scene's design cannot be " +
                'changed in place).',
        });
    });

    return findings;
}
